import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { formatBytes } from '../../../../components/company-data-export/exportFormat';
import { formatClosureDate } from '../../../../components/company-closure/companyClosure';

/**
 * CompanyClosureArchive - Arquivo de segurança do encerramento (Admin Geral)
 *
 * Mostra a situação do arquivo (não gerado, gerando com progresso, falhou,
 * pronto com uma linha de download por parte) e o botão "Gerar arquivo de
 * segurança", liberado só com a carência vencida e o bucket configurado.
 *
 * @param {Object} props
 * @param {Object} props.closure - objeto `closure` de admin/companies/get.php
 * @param {boolean} props.running
 * @param {boolean} props.creating
 * @param {number|null} props.downloadingPart
 * @param {Function} props.onCreate
 * @param {Function} props.onDownload - recebe o índice da parte
 */
function CompanyClosureArchive({ closure, running, creating, downloadingPart, onCreate, onDownload }) {
  const job = closure.archive_job;
  const ready = !!closure.archive_ready;
  const failed = !ready && !running && !!job && (job.status === 'error' || job.status === 'cancelled');
  const canCreate = closure.state === 'expired' && !ready && !running && closure.archive_storage_configured;
  const parts = closure.archive_parts || [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Inventory2OutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Arquivo de segurança
        </Typography>
      </Box>

      {ready && (
        <>
          <Typography variant="body2" color="text.secondary">
            Pronto em {formatClosureDate(closure.archived_at)} · {formatBytes(closure.archive_bytes)}
            {parts.length > 1 ? ` · ${parts.length} partes` : ''}. Guardado por 1 ano.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {parts.map((part) => (
              <Box
                key={part.index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  p: 1.25,
                  border: '1px solid #E8ECF0',
                  borderRadius: '8px',
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                    {part.name || `Parte ${part.index + 1}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatBytes(part.size)}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={downloadingPart === part.index
                    ? <CircularProgress size={14} />
                    : <FileDownloadOutlinedIcon />}
                  disabled={downloadingPart !== null}
                  onClick={() => onDownload(part.index)}
                  sx={{ flexShrink: 0 }}
                >
                  Baixar
                </Button>
              </Box>
            ))}
          </Box>
        </>
      )}

      {running && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
            {job?.current_task || 'Gerando o arquivo de segurança...'}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, Number(job?.progress) || 0))}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      )}

      {failed && (
        <Alert severity="error">
          A última geração não terminou{job.current_task ? `: ${job.current_task}` : '.'} Gere o arquivo novamente.
        </Alert>
      )}

      {!ready && !running && !failed && (
        <Typography variant="body2" color="text.secondary">
          {closure.state === 'expired'
            ? 'Ainda não gerado. A exclusão da empresa só é liberada com o arquivo pronto.'
            : 'Disponível quando a carência terminar.'}
        </Typography>
      )}

      {closure.state === 'expired' && !ready && !closure.archive_storage_configured && (
        <Alert severity="warning">
          Armazenamento remoto não configurado. O arquivo de segurança não pode ser gerado.
        </Alert>
      )}

      {canCreate && (
        <Box>
          <Button
            variant="contained"
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <Inventory2OutlinedIcon />}
            onClick={onCreate}
            disabled={creating}
          >
            Gerar arquivo de segurança
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default CompanyClosureArchive;
